/**
 * The Starfall title's shooting stars.
 *
 * It replaced a CSS streak — a fixed-length line slid across the badge — which
 * read as a brush stroke being dragged, not a star. A shooting star *paints* its
 * trail: the head moves fast, and every point it passes stays lit where it was
 * and fades on its own clock. So nothing here moves a trail. The trail at any
 * moment is the head's own path, sampled backwards in time, each sample as
 * bright as it is young: freshly painted at the head, gone `METEOR_TRAIL_FADE`
 * seconds later. When the head burns out, the last of the trail is still there,
 * fading, which is the moment that sells it.
 *
 * Measured in em of the badge's font, like the snow. Pure apart from the random
 * source it is handed, so it is tested without a canvas or a clock.
 */

export interface Meteor {
  start: number;
  x0: number;
  y0: number;
  /** Velocity, em/s. */
  vx: number;
  vy: number;
  /** Seconds the head burns for. */
  dur: number;
}

export interface MeteorField {
  w: number;
  h: number;
  t: number;
  meteors: Meteor[];
  nextAt: number;
  rand: () => number;
}

export interface TrailSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** 0–1: how bright this piece of trail still is. */
  alpha: number;
}

/** Head speed, em/s. Fast: a shooting star is gone before it is followed. */
export const METEOR_SPEED: readonly [number, number] = [24, 34];
/** How long a painted point of trail takes to fade out, seconds. */
export const METEOR_TRAIL_FADE = 0.6;
/** Dark sky between one star burning out and the next appearing, seconds. */
export const METEOR_GAP: readonly [number, number] = [1.2, 3.4];
/** Downward slope of the path, degrees. */
export const METEOR_ANGLE: readonly [number, number] = [5, 13];
/** The band the stars start in, em from the top of the field. */
export const METEOR_START_BAND: readonly [number, number] = [0.15, 0.6];

const between = (rand: () => number, [lo, hi]: readonly [number, number]): number =>
  lo + rand() * (hi - lo);

export function createMeteorField(w: number, h: number, rand: () => number = Math.random): MeteorField {
  return { w, h, t: 0, meteors: [], nextAt: between(rand, [0.3, 1.2]), rand };
}

function spawn(field: MeteorField): Meteor {
  const { rand, w } = field;
  const speed = between(rand, METEOR_SPEED);
  const angle = (between(rand, METEOR_ANGLE) * Math.PI) / 180;
  const x0 = -0.3 + rand() * w * 0.25;
  // Burns across most of the badge, never all of it the same way.
  const length = w * between(rand, [0.6, 0.95]);
  return {
    start: field.t,
    x0,
    y0: between(rand, METEOR_START_BAND),
    vx: speed * Math.cos(angle),
    vy: speed * Math.sin(angle),
    dur: length / speed,
  };
}

/** Advances the sky by `dt` seconds. A long stall is clamped, never replayed. */
export function stepMeteors(field: MeteorField, dt: number): void {
  field.t += Math.min(Math.max(dt, 0), 0.1);
  field.meteors = field.meteors.filter((m) => field.t < m.start + m.dur + METEOR_TRAIL_FADE);
  if (field.t >= field.nextAt) {
    const m = spawn(field);
    field.meteors.push(m);
    field.nextAt = field.t + m.dur + between(field.rand, METEOR_GAP);
  }
}

/** How bright the head burns `s` seconds into its life: a flare in, a burn-out. */
export function burnAt(m: Meteor, s: number): number {
  if (s < 0 || s > m.dur) return 0;
  const p = s / m.dur;
  if (p < 0.12) return p / 0.12;
  if (p > 0.7) return Math.max(0, (1 - p) / 0.3);
  return 1;
}

/** Where the head is at time `t`, and how bright — or null once it has burnt out. */
export function headAt(m: Meteor, t: number): { x: number; y: number; alpha: number } | null {
  const s = t - m.start;
  if (s < 0 || s > m.dur) return null;
  return { x: m.x0 + m.vx * s, y: m.y0 + m.vy * s, alpha: burnAt(m, s) };
}

/**
 * The trail at time `t`: the path the head has painted, sampled backwards from
 * now, each piece as bright as it is young and as the head was when it painted
 * it. Empty before the star appears and once the last of it has faded.
 */
export function trailAt(m: Meteor, t: number, samples = 28): TrailSegment[] {
  const from = Math.max(m.start, t - METEOR_TRAIL_FADE);
  const to = Math.min(t, m.start + m.dur);
  if (to <= from) return [];
  const out: TrailSegment[] = [];
  const step = (to - from) / samples;
  for (let i = 0; i < samples; i += 1) {
    const a = from + step * i;
    const b = a + step;
    const age = t - b;
    const fade = Math.max(0, 1 - age / METEOR_TRAIL_FADE);
    const alpha = fade * fade * burnAt(m, b - m.start);
    if (alpha <= 0.01) continue;
    out.push({
      x1: m.x0 + m.vx * (a - m.start),
      y1: m.y0 + m.vy * (a - m.start),
      x2: m.x0 + m.vx * (b - m.start),
      y2: m.y0 + m.vy * (b - m.start),
      alpha,
    });
  }
  return out;
}

/** `#rrggbb` → `r, g, b`, for building `rgba()` strings. */
function rgb(hex: string): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '255, 255, 255';
  return `${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}`;
}

/**
 * Draws the sky. `em` is the badge's font size in canvas pixels; `colour` the
 * title's own, which the trail's glow and the head's halo are tinted with.
 */
export function drawMeteors(
  ctx: CanvasRenderingContext2D,
  field: MeteorField,
  em: number,
  colour: string,
): void {
  ctx.clearRect(0, 0, field.w * em, field.h * em);
  const tint = rgb(colour);
  ctx.lineCap = 'round';
  for (const m of field.meteors) {
    const trail = trailAt(m, field.t);
    // A soft coloured glow under the trail, then a thin white-hot core.
    for (const pass of [
      { width: 0.14, colour: tint, strength: 0.35 },
      { width: 0.04, colour: '255, 255, 255', strength: 1 },
    ]) {
      ctx.lineWidth = Math.max(0.7, pass.width * em);
      for (const seg of trail) {
        ctx.strokeStyle = `rgba(${pass.colour}, ${(seg.alpha * pass.strength).toFixed(3)})`;
        ctx.beginPath();
        ctx.moveTo(seg.x1 * em, seg.y1 * em);
        ctx.lineTo(seg.x2 * em, seg.y2 * em);
        ctx.stroke();
      }
    }

    const head = headAt(m, field.t);
    if (head && head.alpha > 0) {
      const x = head.x * em;
      const y = head.y * em;
      const halo = ctx.createRadialGradient(x, y, 0, x, y, 0.45 * em);
      halo.addColorStop(0, `rgba(255, 255, 255, ${head.alpha})`);
      halo.addColorStop(0.25, `rgba(${tint}, ${(0.55 * head.alpha).toFixed(3)})`);
      halo.addColorStop(1, `rgba(${tint}, 0)`);
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(x, y, 0.45 * em, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255, 255, 255, ${head.alpha})`;
      ctx.beginPath();
      ctx.arc(x, y, Math.max(0.8, 0.07 * em), 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
